import { LOCATION_CHANGE, replace } from 'connected-react-router';
import { all, call, fork, put, select, spawn, take } from 'redux-saga/effects';

import * as user from '../user/selectors';
import fetchContentStackContent from '../contentStack/saga';
// Content API disabled - Deployed live application does not have access to prod Content API
// import fetchContentApiContent from '../contentApi/saga';
import { DEFAULT, HOME, LANDING, LOGIN, LOGIN_CONSENTS } from '../../routes/paths';
import { ERROR, setMessage } from '../../containers/FormMessage/actions';
import { INIT, SERVICE_UNAVAILABLE, storageStatus } from './actions';
import { getPathname } from './selectors';
import { getPersistedUserState } from '../user/storage';
import { getStoredMobileNumber } from '../auth/storage';
import { isLocalStorageAvailable } from './storage';
import { logIn, setAnonymous } from '../user/actions';
import { mobileNumberValid } from '../auth/actions';
import { visibilityChangeChannel } from './channels';

export function* appDefaultEntryPath() {
  const welcomePaths = [
    DEFAULT,
    LANDING,
    LOGIN
  ];

  const isAuthorized = yield select(user.isAuthorized);
  const isAuthenticated = yield select(user.isAuthenticated);
  const entryPath = yield select(getPathname);

  if (isAuthorized && welcomePaths.includes(entryPath)) {
    yield put(replace((isAuthenticated) ? LOGIN_CONSENTS : HOME));
  }
}

export function* userInit() {
  const { isAuthorized, isAuthenticated } = yield call(getPersistedUserState);

  if (isAuthorized && isAuthenticated) {
    yield put(logIn());
  } else if (isAuthorized) {
    yield put(setAnonymous());
  }
}

export function* mobileNumberFill() {
  const { isAuthorized, isAuthenticated } = yield call(getPersistedUserState);

  if (!isAuthorized && !isAuthenticated) {
    const homePaths = [
      DEFAULT,
      LANDING
    ];
    const entryPath = yield select(getPathname);
    const mobileNumber = yield call(getStoredMobileNumber);

    if (mobileNumber) {
      yield put(mobileNumberValid(mobileNumber));

      if (homePaths.includes(entryPath)) {
        yield put(replace(LOGIN));
      }
    }
  }
}

export function* appInit() {
  const localStorageAvailable = yield call(isLocalStorageAvailable);
  yield put(storageStatus(localStorageAvailable));
  if (!localStorageAvailable) {
    yield put(setMessage(
      ERROR,
      'Private Browsing must be turned off for this site to work correctly',
      null,
      true,
      'app_errors'
    ));
  }
}

export function* init() {
  yield call(appInit);
  yield call(userInit);
  yield call(appDefaultEntryPath);
  yield call(mobileNumberFill);
  yield call(fetchContentStackContent);
  // Content API disabled - Deployed live application does not have access to prod Content API
  // yield call(fetchContentApiContent);
}

function* watchInit() {
  yield take(INIT);
  yield fork(init);
}

function* redirectToDefaultPath() {
  while (yield take(LOCATION_CHANGE)) {
    yield call(appDefaultEntryPath);
    yield call(mobileNumberFill);
  }
}

function* watchServiceUnavailable() {
  while (yield take(SERVICE_UNAVAILABLE)) {
    yield call([location, location.assign], '/');
  }
}

function* watchAppOutOfDate() {
  const lastAppStartTime = Date.now();
  const channel = yield call(visibilityChangeChannel);

  /* eslint-disable no-constant-condition */
  while (true) {
    const { hidden } = yield take(channel);

    if (!hidden) {
      const secondsBeforeReloadRequired = 24 * 60 * 60;
      const secondsSinceLastAppStart = (Date.now() - lastAppStartTime) / 1000;

      if (secondsSinceLastAppStart > secondsBeforeReloadRequired) {
        window.location.reload(true);
      }
    }
  }
}

export default function* root() {
  yield all([
    spawn(watchInit),
    spawn(redirectToDefaultPath),
    spawn(watchServiceUnavailable),
    spawn(watchAppOutOfDate)
  ]);
}
/* eslint-enable no-constant-condition */
